"use client";

import { ArrowUpRight, Brush, Image as ImageIcon, Layers, Package, Shapes, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useInView } from "@/hooks/useInView";
import { ScrollReveal } from "./ScrollReveal";
import { LandingSectionHeader } from "./LandingSectionHeader";
import { LANDING_TOOL_COUNT } from "./LandingToolsGrid";

// Spell out the tool count so the headline still reads as a sentence
// ("Eighteen tools") while staying derived from LANDING_TOOL_COUNT — the word
// never needs hand-updating when a tool is added or removed.
const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
  "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
  "seventeen", "eighteen", "nineteen",
];
const TENS_WORDS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

function spellOut(n: number): string {
  if (n < 20) return NUMBER_WORDS[n] ?? String(n);
  if (n < 100) {
    const tens = TENS_WORDS[Math.floor(n / 10)];
    const ones = n % 10;
    return ones ? `${tens}-${NUMBER_WORDS[ones]}` : tens;
  }
  return String(n);
}

const TOOL_COUNT_WORD = (() => {
  const word = spellOut(LANDING_TOOL_COUNT);
  return word.charAt(0).toUpperCase() + word.slice(1);
})();

export function LandingConnectedPlatform() {
  const [sectionRef, isVisible] = useInView<HTMLElement>(0.08);

  return (
    <section ref={sectionRef} className="bg-[hsl(var(--lb-bg-soft))] py-8 sm:py-14">
      <div className="lb-container">
        <ScrollReveal>
          <LandingSectionHeader
            eyebrow="Shared library"
            title={`One cloud library. ${TOOL_COUNT_WORD} tools wired into it.`}
            copy="Save once and every tool can read it. AI generations, paint passes, packed builds — they all live in the same project, so each step picks up where the last left off."
            align="center"
          />
        </ScrollReveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <FeatureCard
            index={0}
            isVisible={isVisible}
            eyebrow="Cross-tool"
            headline="One library. Every tool."
            subhead="Save in one tool, open it in the next — no re-uploading, no exports to disk."
            ctaLabel="See how it works"
            ctaHref="/my-projects"
            visual={<TransfersVisual />}
          />
          <FeatureCard
            index={1}
            isVisible={isVisible}
            eyebrow="AI workflow"
            headline="AI-powered workflow."
            subhead="Model Forge, Asset Art, Badge Maker — chained through one cloud project."
            ctaLabel="Open Model Forge"
            ctaHref="/model-forge"
            visual={<AiPipelineVisual />}
          />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  index,
  isVisible,
  eyebrow,
  headline,
  subhead,
  ctaLabel,
  ctaHref,
  visual,
}: {
  index: number;
  isVisible: boolean;
  eyebrow: string;
  headline: string;
  subhead: string;
  ctaLabel: string;
  ctaHref: string;
  visual: React.ReactNode;
}) {
  return (
    <Link
      to={ctaHref}
      className="lb-line-card group relative block overflow-hidden p-8 sm:p-10 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(0,0,0,0.10)]"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "none" : "translateY(28px)",
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${120 + index * 120}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${120 + index * 120}ms, box-shadow 0.4s ease`,
      }}
    >
      <div className="flex items-start justify-between">
        <p className="font-mono text-sm uppercase tracking-[0.1em] text-black/35">{eyebrow}</p>
        <ArrowUpRight className="h-7 w-7 text-black/28 transition group-hover:text-black" />
      </div>

      <div className="mt-8 h-[170px] w-full overflow-hidden rounded-2xl border border-black/10 bg-white/60 motion-reduce:animate-none">
        {visual}
      </div>

      <h3 className="mt-10 max-w-xl text-[2.55rem] font-semibold leading-[0.98] tracking-[-0.06em] text-black sm:text-[3rem]">
        {headline}
      </h3>
      <p className="mt-5 max-w-xl text-lg leading-[1.45] tracking-[-0.03em] text-black/58 sm:text-xl">
        {subhead}
      </p>

      <span className="mt-8 inline-flex items-center gap-1.5 text-base font-medium tracking-[-0.02em] text-black/85 transition group-hover:text-black">
        {ctaLabel}
        <ArrowUpRight className="h-4 w-4" />
      </span>
    </Link>
  );
}

function TransfersVisual() {
  return (
    <div className="relative h-full w-full">
      <div className="absolute inset-0 flex items-center justify-between px-8">
        <ToolNode label="UV Studio" Icon={Shapes} />

        <div className="relative mx-4 h-full flex-1">
          <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-black/10 via-black/40 to-black/10" />
          <div className="absolute left-[4%] right-[4%] top-1/2 hidden -translate-y-1/2 motion-safe:block motion-reduce:hidden">
            <FloatingFormatChip label=".yft" delay="0s" tone="text-black/80" />
            <FloatingFormatChip label=".png" delay="1.4s" tone="text-black/65" />
            <FloatingFormatChip label=".dds" delay="2.8s" tone="text-black/50" />
          </div>
          <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-black/38 motion-safe:hidden">
            <span>.yft</span>
            <span>.png</span>
            <span>.dds</span>
          </div>
        </div>

        <ToolNode label="Paint Booth" Icon={Brush} highlight />
      </div>
    </div>
  );
}

function FloatingFormatChip({
  label,
  delay,
  tone,
}: {
  label: string;
  delay: string;
  tone: string;
}) {
  return (
    <span
      className={cn(
        "absolute left-0 top-1/2 inline-flex -translate-y-1/2 items-center rounded-full border border-black/12 bg-white/88 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] shadow-[0_8px_22px_rgba(0,0,0,0.08)] backdrop-blur-sm motion-safe:animate-glyph-drift",
        tone,
      )}
      style={{ animationDelay: delay }}
    >
      {label}
    </span>
  );
}

function ToolNode({
  label,
  Icon,
  highlight = false,
}: {
  label: string;
  Icon: LucideIcon;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span
        className={cn(
          "grid h-14 w-14 place-items-center rounded-2xl border bg-white",
          highlight ? "border-black/40 text-black shadow-[0_8px_22px_rgba(0,0,0,0.08)]" : "border-black/15 text-black/70",
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-black/55">
        {label}
      </span>
    </div>
  );
}

function AiPipelineVisual() {
  const nodes: { Icon: LucideIcon; label: string; glow: string }[] = [
    { Icon: Sparkles, label: "Prompt", glow: "motion-safe:animate-node-glow-1" },
    { Icon: Layers, label: "Model", glow: "motion-safe:animate-node-glow-2" },
    { Icon: ImageIcon, label: "Livery", glow: "motion-safe:animate-node-glow-3" },
    { Icon: Package, label: "Pack", glow: "motion-safe:animate-node-glow-4" },
  ];
  return (
    <div className="relative h-full w-full px-6">
      <div className="absolute left-[10%] right-[10%] top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-black/10 via-black/40 to-black/10" />
      <span
        aria-hidden
        className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-black shadow-[0_0_18px_4px_rgba(0,0,0,0.25)] motion-safe:animate-spark-travel motion-reduce:hidden"
      />
      <div className="relative flex h-full items-center justify-between">
        {nodes.map(({ Icon, label, glow }) => (
          <div key={label} className="flex flex-col items-center gap-2">
            <span
              className={cn(
                "grid h-12 w-12 place-items-center rounded-2xl border border-black/15 bg-white text-black/80 motion-reduce:animate-none",
                glow,
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-black/55">
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
